import { useForm } from 'react-hook-form'

import { ApolloError } from '@apollo/client'
import { yupResolver } from '@hookform/resolvers/yup'

import { useCreateUserMutation, User, UserDataDocument, useUserDataQuery } from '~/app/generated/graphql'
import { gt } from '~/lib/gt'

import AppleLoginButton from '../AppleLogin/AppleLoginButtonView'
import { Button } from '../Button/Button'
import Checkbox from '../Checkbox'
import { FormInput } from '../FormInput/FormInput'
import GoogleLoginButton from '../GoogleLoginButton'
import { formatErrorMessages } from '../LoginModal/formatErrorMessages'
import { useToast } from '../Toast'

import styles from './RegisterModal.module.css'
import { registerSchema } from './RegisterModalUtils'

export type RegisterSignInCallback = (user: Pick<User, 'id'> | null, social: boolean) => void

type RegisterModalProps = {
  handleClose: () => void
  onLoginLinkClick?: () => void
  onSuccess?: RegisterSignInCallback
}

export const RegisterModal = ({ handleClose, onLoginLinkClick, onSuccess }: RegisterModalProps) => {
  return (
    <div className={styles.registerModal}>
      <div className={styles.formWrapper}>
        <RegisterForm handleClose={handleClose} onLoginLinkClick={onLoginLinkClick} onSuccess={onSuccess} />
      </div>
      <div className={styles.imageWrapper}>
        <div className={styles.textWrapper}>
          <h3 className={styles.h3}>{gt.tp('RegisterForm', 'Register your Guest Account today!')}</h3>
          <h5 className={styles.h5}>✔️ {gt.tp('RegisterForm', 'Invite your loved ones!')}</h5>
          <h5 className={styles.h5}>✔️ {gt.tp('RegisterForm', 'Book your favorite menu. Discover new restaurants.')}</h5>
          <h5 className={styles.h5}>
            ✔️ {gt.tp('RegisterForm', 'Thousands of signature dishes, hundreds of best restaurants awaits for you!')}
          </h5>
          <h5 className={styles.h5}>✔️ {gt.tp('RegisterForm', 'Benefit from discounts and offers thanks to RestaurantClub friends.')}</h5>
        </div>
      </div>
    </div>
  )
}

export const RegisterForm = ({ handleClose, onLoginLinkClick, onSuccess }: RegisterModalProps) => {
  const { refetch } = useUserDataQuery()
  const [createUser] = useCreateUserMutation()
  const toast = useToast()

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      password: '',
      marketingConsentAccepted: false,
      regulationConsentAccepted: false,
    },
    resolver: yupResolver(registerSchema),
  })

  const handleErrors = (error: ApolloError) => {
    const errors = formatErrorMessages(error)
    errors?.map(error =>
      toast?.add({
        message: error,
        type: 'error',
        containerSelector: '#RegisterToastContainer',
        size: 'small',
      })
    )
  }

  const onSocialSignUp: RegisterSignInCallback = async user => {
    await refetch()
    if (user) onSuccess?.(user, true)
    handleClose && handleClose()
  }

  const onSubmit = async (values: any) => {
    await createUser({
      variables: {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        phoneNumber: values.phoneNumber,
        marketingConsentAccepted: values.marketingConsentAccepted,
      },
      onCompleted: data => {
        onSuccess && onSuccess(data?.createUser.user ?? null, false)
      },
      refetchQueries: [{ query: UserDataDocument }, 'UserData'],
      onError: error => handleErrors(error),
    })
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
      <h2 className={styles.title}>{gt.tp('RegisterForm', 'Register')}</h2>
      <h5 className={styles.noAccount}>
        {gt.tp('RegisterForm', 'Already have an account?')}{' '}
        <span className={styles.link} onClick={onLoginLinkClick}>
          {gt.tp('RegisterForm', 'Log in here')}
        </span>
      </h5>
      <div id='RegisterToastContainer' className={styles.toastContainer} />
      <div className={styles.styledInputs}>
        <FormInput
          {...register('firstName')}
          type={'text'}
          label={gt.tp('RegisterForm', 'First name*')}
          className={styles.input}
          errorMessage={errors.firstName?.message}
        />
        <FormInput
          {...register('lastName')}
          type={'text'}
          label={gt.tp('RegisterForm', 'Last name*')}
          className={styles.input}
          errorMessage={errors.lastName?.message}
        />
        <FormInput
          {...register('email')}
          type={'email'}
          label={gt.tp('RegisterForm', 'Email*')}
          className={styles.input}
          errorMessage={errors.email?.message}
        />
        <FormInput
          {...register('phoneNumber')}
          type={'tel'}
          label={gt.tp('RegisterForm', 'Phone')}
          className={styles.input}
          errorMessage={errors.phoneNumber?.message}
        />
        <FormInput
          {...register('password')}
          type={'password'}
          label={gt.tp('RegisterForm', 'Password*')}
          className={styles.input}
          errorMessage={errors.password?.message}
        />
      </div>
      <div className={styles.checkboxWrapper}>
        <Checkbox
          name='marketingConsentAccepted'
          control={control}
          label={
            <span
              dangerouslySetInnerHTML={{
                __html: `${gt.tp(
                  'RegisterForm',
                  '{{ thankYouBold }} - I want to use DISCOUNT CODES, receive notifications about Festivals, and get the "RESTAURANT RECIPES" eBook {{ valuedAtBold }}. Therefore, I consent to receiving commercial information',
                  {
                    thankYouBold: `<b>${gt.tp('RegisterForm', 'Yes, thank you')}</b>`,
                    valuedAtBold: `<b>${gt.tp('RegisterForm', '(valued at PLN 78)')}</b>`,
                  }
                )}`,
              }}
            />
          }
          showMore={gt.tp(
            'RegisterForm',
            'from Restaurant Club sp. z o.o. to the contact details I provided and to the use of these details for direct marketing purposes via electronic communication devices (email, social media platforms, SMS to the provided phone number, and phone contact).'
          )}
          error={!!errors.marketingConsentAccepted}
          disabled={isSubmitting}
        />
        <Checkbox
          name='regulationConsentAccepted'
          control={control}
          label={
            <>
              {gt.tp('RegisterForm', '* I accept the')}{' '}
              <a href={'/regulations'} className={styles.linkCheckbox} target='_blank'>
                {gt.tp('CheckoutPage', 'Terms and Conditions')}
              </a>{' '}
              {gt.tp('RegisterForm', 'and have read the')}{' '}
              <a href={'/privacy-policy'} className={styles.linkCheckbox} target='_blank'>
                {gt.tp('CheckoutPage', 'Privacy Policy')}.
              </a>
            </>
          }
          error={!!errors.regulationConsentAccepted}
          disabled={isSubmitting}
        />
      </div>
      <p className={styles.consentNotice}>
        {gt.tp(
          'ConsentNotice',
          'If you do not agree to the communication, you will lose the ability to contact you about your bookings. It will also prevent us from communicating booking benefits to you.'
        )}
      </p>
      <Button className={styles.registerButton} type='submit' disabled={!isValid || isSubmitting} withArrow={false}>
        {gt.tp('RegisterForm', 'Register')}
      </Button>
      <div className={styles.otherLogin}>{gt.tp('RegisterForm', 'or')}</div>
      <div className={styles.otherLoginButtons}>
        <GoogleLoginButton onLogin={onSocialSignUp} label={gt.tp('Register', 'Sign up with Google')} />
        <AppleLoginButton onLogin={onSocialSignUp} label={gt.tp('Register', 'Sign up with Apple')} />
      </div>
    </form>
  )
}
